import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { post } from "../../lib/api";
import { DEPOT_NAME_MAX, depotNameError, timezoneError, timezoneOptions } from "../../lib/station";
import type { Depot } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { Field, SelectField } from "../ui/Input";
import { Modal } from "../ui/Modal";

/**
 * Creating a depot — a physical site.
 *
 * The timezone decides something that cannot be changed later, and the form says
 * so rather than leaving an operator to find out: it is where this site's allowance
 * months and weeks start, and moving it would move period boundaries that energy
 * figures have already been reported against.
 *
 * There is no unknown-card choice. Unseen cards are always refused now, and the
 * server's default says so without the form sending anything.
 *
 * A modal over the Depots page, rather than inside the register-a-station form. Sharing
 * that form's space meant either nesting one `<form>` in another — invalid, and it
 * broke submission outright — or swapping the station fields out and taking the
 * charge point id off screen mid-task. A site is its own decision and gets its own
 * place to be made.
 */

export interface CreateDepotProps {
  readonly open: boolean;
  readonly onCreated: (depot: Depot) => void;
  readonly onCancel: () => void;
}

export function CreateDepot({ open, onCreated, onCancel }: CreateDepotProps) {
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      eyebrow="Depots"
      title="New depot"
    >
      {/* Mounted only while open, so a cancelled attempt does not come back half-typed. */}
      {open ? <CreateDepotForm onCreated={onCreated} onCancel={onCancel} /> : null}
    </Modal>
  );
}

function CreateDepotForm({ onCreated, onCancel }: Omit<CreateDepotProps, "open">) {
  const queryClient = useQueryClient();

  const zones = useMemo(() => timezoneOptions(), []);
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState(zones[0] ?? "UTC");

  const create = useMutation({
    mutationFn: (body: unknown) => post<Depot>("/depots", body),
    onSuccess: (depot) => {
      // The fleet page's depot filter and the station form's picker both read this.
      void queryClient.invalidateQueries({ queryKey: ["depots"] });
      onCreated(depot);
    },
  });

  const nameIssue = depotNameError(name);
  const zoneIssue = timezoneError(timezone);
  const canSubmit =
    name.trim() !== "" && nameIssue === undefined && zoneIssue === undefined && !create.isPending;

  return (
    <div className="flex flex-col gap-5">
      {create.isError ? <ErrorNotice error={create.error} /> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          create.mutate({ name: name.trim(), timezone });
        }}
        aria-label="New depot"
        className="flex flex-col gap-5"
      >
        <Field
          label="Site name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Toul Kork Yard"
          maxLength={DEPOT_NAME_MAX}
          required
          autoFocus
          {...(nameIssue === undefined ? {} : { error: nameIssue })}
        />

        <SelectField
          label="Timezone"
          value={timezone}
          onChange={(event) => setTimezone(event.target.value)}
          required
        >
          {zones.map((zone) => (
            <option key={zone} value={zone}>
              {zone}
            </option>
          ))}
        </SelectField>

        <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
          The timezone is where this site's allowance months and weeks begin, and it cannot be
          changed afterwards — moving it would move boundaries that energy figures have already been
          reported against.
        </p>

        <div className="flex items-center justify-end gap-2 border-t border-rule pt-4">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit} pending={create.isPending}>
            Create depot
          </Button>
        </div>
      </form>
    </div>
  );
}
